// src/components/admin/UserManagement.js
import { Calendar, Clock, Download, Loader2, Mail, Phone, Search } from "lucide-react";
import React, { useEffect, useState } from "react";
import axios from "../../utils/axiosHandler";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tableLoading, setTableLoading] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`/api/admin/users?page=${currentPage}&limit=10&search=${search}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.data;

      if (response.status !== 200) {
        console.error("Error:", response.data.error);
        setUsers([]);
        setTotalPages(1);
        return;
      }

      // Make sure we process the users data properly
      const processedUsers = (data.users || []).map(user => {
        // Make sure lastLogin is properly recognized as a date if it exists
        if (user.lastLogin) {
          // No need to modify it, but we can validate it's a valid date
          const loginDate = new Date(user.lastLogin);
          if (isNaN(loginDate.getTime())) {
            console.warn(`Invalid lastLogin date for user ${user._id}:`, user.lastLogin);
            user.lastLogin = null;
          }
        }

        return user;
      });

      setUsers(processedUsers);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Create a debounce timer to avoid excessive API calls
    const timer = setTimeout(() => {
      if (debouncedSearch !== search) {
        setSearch(debouncedSearch);
        setCurrentPage(1); // Reset to first page on new search
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [debouncedSearch]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem('token');

      const response = await axios.get('/api/admin/users/export', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob', // crucial for downloading files
      });

      // Extract filename from headers if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'users-export.xlsx';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      } else {
        filename = `users-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      }

      // Create a download link for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting users:', error);
      alert('Failed to export users data. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <h2 className="text-2xl font-semibold">User Management</h2>
      <div className="p-4 space-y-6 bg-gray-50 rounded-xl">
        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
          <form onSubmit={handleSearch} className="flex flex-1 gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search users..."
                value={debouncedSearch}
                onChange={
                  (e) => setDebouncedSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Search
            </button>
          </form>
          <button
            onClick={handleExport}
            disabled={exportLoading || users.length === 0}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-400"
          >
            {exportLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Export
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="hidden md:block"> {/* Table only visible on medium screens and up */}
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users && users.length > 0 ? (
                users.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <Mail className="h-4 w-4 mr-1" />
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <Phone className="h-4 w-4 mr-1" />
                        {user.phone || 'N/A'}
                      </div>
                      {user.company && (
                        <div className="text-sm text-gray-500 mt-1">
                          {user.company}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.defaultAddress ? (
                        // Find and show only the default address
                        user.addresses.filter(addr =>
                          addr._id.toString() === user.defaultAddress.toString()
                        ).map(address => (
                          <div key={address._id} className="text-sm text-gray-500">
                            {address.street}<br />
                            {address.city}, {address.state} {address.postalCode}<br />
                            {address.country}
                            <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">Default</span>
                          </div>
                        ))
                      ) : (
                        user.addresses && user.addresses.length > 0 ? (
                          <div className="text-sm text-gray-500">
                            {user.addresses[0].street}<br />
                            {user.addresses[0].city}, {user.addresses[0].state} {user.addresses[0].postalCode}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">No address</span>
                        )
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.isAdmin
                          ? "bg-purple-100 text-purple-800"
                          : "bg-green-100 text-green-800"
                          }`}
                      >
                        {user.isAdmin ? "Admin" : "User"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {user.lastLogin
                          ? new Date(user.lastLogin).toLocaleString()
                          : 'Never'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {new Date(user.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile card view - only visible on small screens */}
        <div className="md:hidden">
          {users && users.length > 0 ? (
            users.map((user) => (
              <div key={user._id} className="border-b border-gray-200 p-4">
                <div className="mb-2">
                  <div className="text-base font-medium text-gray-900">
                    {user.firstName} {user.lastName}
                  </div>
                  <div className="flex items-center text-sm text-gray-500 mt-1">
                    <Mail className="h-4 w-4 mr-1" />
                    {user.email}
                  </div>
                </div>
                <div className="flex flex-wrap gap-y-2 text-sm">
                  <div className="w-full flex items-center text-gray-500">
                    <Phone className="h-4 w-4 mr-1" />
                    {user.phone || 'N/A'}
                  </div>
                  {user.addresses && user.addresses.length > 0 && (
                    <div className="w-full text-sm text-gray-500 mt-1">
                      <div className={user.defaultAddress ? 'font-medium' : ''}>
                        {user.addresses[0].street}, {user.addresses[0].city}
                        {user.defaultAddress && <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">Default</span>}
                      </div>
                    </div>
                  )}
                  <div className="w-full flex items-center text-gray-500">
                    <Clock className="h-4 w-4 mr-1" />
                    Last Login: {user.lastLogin
                      ? new Date(user.lastLogin).toLocaleString().split(',')[0]
                      : 'Never'}
                  </div>
                  <div className="w-full flex items-center justify-between">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.isAdmin
                        ? "bg-purple-100 text-purple-800"
                        : "bg-green-100 text-green-800"
                        }`}
                    >
                      {user.isAdmin ? "Admin" : "User"}
                    </span>
                    <div className="flex items-center text-gray-500">
                      <Calendar className="h-4 w-4 mr-1" />
                      {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-4 text-center text-gray-500">
              No users found
            </div>
          )}
        </div>

        {users && users.length > 0 && (
          <div className="bg-white px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200">
            <div className="flex w-full justify-between sm:hidden mb-4">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page{" "}
                  <span className="font-medium">{currentPage}</span> of{" "}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav
                  className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                  aria-label="Pagination"
                >
                  {totalPages <= 5 ? (
                    // Show all page numbers if 5 or fewer
                    [...Array(totalPages)].map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentPage(idx + 1)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === idx + 1
                          ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                          : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}
                      >
                        {idx + 1}
                      </button>
                    ))
                  ) : (
                    // Show limited page numbers with ellipsis for better mobile experience
                    <>
                      <button
                        onClick={() => setCurrentPage(1)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === 1
                          ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                          : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}
                      >
                        1
                      </button>
                      {currentPage > 3 && (
                        <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      )}
                      {currentPage !== 1 && currentPage !== totalPages && (
                        <button
                          className="z-10 bg-blue-50 border-blue-500 text-blue-600 relative inline-flex items-center px-4 py-2 border text-sm font-medium"
                        >
                          {currentPage}
                        </button>
                      )}
                      {currentPage < totalPages - 2 && (
                        <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      )}
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === totalPages
                          ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                          : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;